import {
  ArrowLeft,
  ChatCircle,
  Flag,
  UserMinus,
  UserPlus,
} from '@phosphor-icons/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ErrorState } from '@/components/error-state'
import { LoadingState } from '@/components/loading-state'
import { ApiError } from '@/lib/api/errors'
import { friendlyErrorKey } from '@/lib/api/friendly-errors'
import { useSessionStore } from '@/lib/auth/session-store'
import { usePersonNameFormatter } from '@/lib/users/use-person-name'
import { KuniriIcons, kuniriIconWeight } from '@/lib/icons'

import {
  profileSocialApi,
  type PublicProfile,
  type RelationshipResponse,
  type ReportReason,
} from '../api'
import { ConfirmDialog } from '../components/confirm-dialog'
import { ProfileMetrics } from '../components/profile-metrics'
import { PullToRefresh } from '../components/pull-to-refresh'
import { RefreshButton } from '../components/refresh-button'
import { ReportDialog } from '../components/report-dialog'
import { UserAvatar } from '../components/user-avatar'
import { useIsDesktop } from '../hooks'

type ProfileState =
  | { status: 'loading' }
  | { status: 'ready'; profile: PublicProfile }
  | { status: 'unavailable' }
  | { status: 'error'; error: unknown }

type RelationshipState =
  | { status: 'loading' }
  | { status: 'ready'; relationship: RelationshipResponse }
  | { status: 'error' }

interface NoticeState {
  tone: 'success' | 'error'
  key: string
}

/**
 * `/users/:id` (UI_PARITY §3.17): public projection, rating, shared-activity
 * count, friend actions, message hand-off to the private conversation API,
 * report and block/unblock with confirmation. The contract exposes no public
 * activity list for a user, only `sharedActivities`, so that count is the only
 * activity information rendered.
 */
export function PublicProfileScreen() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const sessionUserId = useSessionStore((state) => state.user?.id ?? null)
  const userId = id?.trim() ? id : null
  const isSelf = Boolean(userId && sessionUserId && userId === sessionUserId)

  const [state, setState] = useState<ProfileState>({ status: 'loading' })
  const [relationship, setRelationship] = useState<RelationshipState>({
    status: 'loading',
  })
  // D-101: the accepted-relationship answer unmasks the pair straight away;
  // everyone else waits for the friend index or stays abbreviated.
  const formatPerson = usePersonNameFormatter(
    useMemo(
      () =>
        relationship.status === 'ready' &&
        relationship.relationship.status === 'accepted' &&
        userId
          ? new Set([userId.toLowerCase()])
          : null,
      [relationship, userId],
    ),
  )

  const [reloadKey, setReloadKey] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const [notice, setNotice] = useState<NoticeState | null>(null)
  const [blocked, setBlocked] = useState(false)
  const [confirm, setConfirm] = useState<'block' | 'unblock' | null>(null)
  const [confirmBusy, setConfirmBusy] = useState(false)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const [reportOpen, setReportOpen] = useState(false)
  const [reportBusy, setReportBusy] = useState(false)
  const [reportError, setReportError] = useState<string | null>(null)
  const [friendBusy, setFriendBusy] = useState(false)
  const [friendError, setFriendError] = useState<string | null>(null)
  const [messageBusy, setMessageBusy] = useState(false)
  const hasProfileRef = useRef(false)

  useEffect(() => {
    if (!userId || isSelf) return
    const controller = new AbortController()

    void profileSocialApi
      .getPublicProfile(userId, { signal: controller.signal })
      .then((profile) => {
        if (controller.signal.aborted) return
        hasProfileRef.current = true
        setState({ status: 'ready', profile })
        setRelationship({ status: 'loading' })
        return profileSocialApi
          .getRelationship(userId, { signal: controller.signal })
          .then((data) => {
            if (!controller.signal.aborted) {
              setRelationship({ status: 'ready', relationship: data })
            }
          })
          .catch(() => {
            if (!controller.signal.aborted) {
              setRelationship({ status: 'error' })
            }
          })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        if (error instanceof ApiError && error.status === 404) {
          setState({ status: 'unavailable' })
          return
        }
        if (hasProfileRef.current) {
          setNotice({ tone: 'error', key: friendlyErrorKey(error) })
          return
        }
        setState({ status: 'error', error })
      })
      .finally(() => {
        if (!controller.signal.aborted) setRefreshing(false)
      })

    return () => controller.abort()
  }, [isSelf, reloadKey, userId])

  const reloadRelationship = useCallback(async () => {
    if (!userId) return
    try {
      const data = await profileSocialApi.getRelationship(userId)
      setRelationship({ status: 'ready', relationship: data })
    } catch {
      setRelationship({ status: 'error' })
    }
  }, [userId])

  const refresh = useCallback(() => {
    if (hasProfileRef.current) setRefreshing(true)
    else setState({ status: 'loading' })
    setReloadKey((key) => key + 1)
  }, [])

  const sendFriendRequest = useCallback(async () => {
    if (!userId || friendBusy) return
    setFriendBusy(true)
    setFriendError(null)
    try {
      await profileSocialApi.sendFriendRequest(userId)
      await reloadRelationship()
      setNotice({
        tone: 'success',
        key: 'profileSocial.public.requestSentNotice',
      })
    } catch (error) {
      setFriendError(friendlyErrorKey(error))
    } finally {
      setFriendBusy(false)
    }
  }, [friendBusy, reloadRelationship, userId])

  const decideRequest = useCallback(
    async (accept: boolean) => {
      if (friendBusy) return
      const current = relationship.status === 'ready' ? relationship.relationship : null
      if (!current?.requestId) return
      setFriendBusy(true)
      setFriendError(null)
      try {
        if (accept) {
          await profileSocialApi.acceptFriendRequest(current.requestId)
        } else {
          await profileSocialApi.rejectFriendRequest(current.requestId)
        }
        await reloadRelationship()
        setNotice({
          tone: 'success',
          key: accept
            ? 'profileSocial.public.acceptedNotice'
            : 'profileSocial.public.rejectedNotice',
        })
      } catch (error) {
        setFriendError(friendlyErrorKey(error))
      } finally {
        setFriendBusy(false)
      }
    },
    [friendBusy, relationship, reloadRelationship],
  )

  const openMessage = useCallback(async () => {
    if (!userId || messageBusy) return
    setMessageBusy(true)
    setFriendError(null)
    try {
      const conversation = await profileSocialApi.openConversation(userId)
      navigate(`/messages/${conversation.id}`)
    } catch (error) {
      setFriendError(friendlyErrorKey(error))
    } finally {
      setMessageBusy(false)
    }
  }, [messageBusy, navigate, userId])

  const submitReport = useCallback(
    async (reason: ReportReason, description: string) => {
      if (!userId) return
      setReportBusy(true)
      setReportError(null)
      try {
        await profileSocialApi.report({
          targetType: 'user',
          targetId: userId,
          reason,
          ...(description ? { description } : {}),
        })
        setReportOpen(false)
        setNotice({
          tone: 'success',
          key: 'profileSocial.public.reportSuccess',
        })
      } catch (error) {
        setReportError(friendlyErrorKey(error))
      } finally {
        setReportBusy(false)
      }
    },
    [userId],
  )

  const confirmBlockState = useCallback(async () => {
    if (!userId || !confirm) return
    setConfirmBusy(true)
    setConfirmError(null)
    try {
      if (confirm === 'block') {
        await profileSocialApi.blockUser(userId)
        setBlocked(true)
        setNotice({ tone: 'success', key: 'profileSocial.public.blockSuccess' })
      } else {
        await profileSocialApi.unblockUser(userId)
        setBlocked(false)
        setNotice({
          tone: 'success',
          key: 'profileSocial.public.unblockSuccess',
        })
        await reloadRelationship()
      }
      setConfirm(null)
    } catch (error) {
      setConfirmError(friendlyErrorKey(error))
    } finally {
      setConfirmBusy(false)
    }
  }, [confirm, reloadRelationship, userId])

  if (!userId) return <Navigate to="/friends" replace />
  if (isSelf) return <Navigate to="/profile" replace />

  const backLink = (
    <Link to="/friends" className="btn-ghost px-0">
      <ArrowLeft size={18} weight={kuniriIconWeight} aria-hidden="true" />
      {t('profileSocial.public.back')}
    </Link>
  )

  const header = (
    <div className="flex items-center justify-between gap-3">
      {backLink}
      {isDesktop && !blocked ? (
        <RefreshButton
          onRefresh={refresh}
          refreshing={refreshing || state.status === 'loading'}
          labelKey="profileSocial.public.refresh"
        />
      ) : null}
    </div>
  )

  if (state.status === 'loading') {
    return (
      <main
        id="main-content"
        tabIndex={-1}
        className="min-h-dvh bg-paper px-4 py-8 md:px-8"
      >
        <div className="mx-auto w-full max-w-[720px] space-y-5">
          {header}
          <h1 className="sr-only">{t('screens.user.title')}</h1>
          <LoadingState variant="profile" labelKey="profileSocial.public.loading" />
        </div>
      </main>
    )
  }

  if (state.status === 'unavailable') {
    return (
      <main
        id="main-content"
        tabIndex={-1}
        className="min-h-dvh bg-paper px-4 py-8 md:px-8"
      >
        <div className="mx-auto w-full max-w-[720px] space-y-5">
          {header}
          <div className="rounded-card border border-line bg-white p-6 text-center">
            <span
              className="mx-auto grid size-14 place-items-center rounded-full bg-mint text-teal"
              aria-hidden="true"
            >
              <KuniriIcons.userMinus
                size={26}
                weight={kuniriIconWeight}
              />
            </span>
            <h1 className="mt-4 text-[clamp(1.7rem,4vw,2.2rem)]">
              {t('profileSocial.public.unavailableTitle')}
            </h1>
            <p className="mx-auto mt-2 max-w-[52ch] text-[15px] leading-relaxed text-muted">
              {t('profileSocial.public.unavailableBody')}
            </p>
            <Link to="/friends" className="btn-primary mt-6">
              {t('profileSocial.public.back')}
            </Link>
          </div>
        </div>
      </main>
    )
  }

  if (state.status === 'error') {
    return (
      <main
        id="main-content"
        tabIndex={-1}
        className="min-h-dvh bg-paper px-4 py-8 md:px-8"
      >
        <div className="mx-auto w-full max-w-[720px] space-y-5">
          {header}
          <h1 className="sr-only">{t('screens.user.title')}</h1>
          <ErrorState
            error={state.error}
            titleKey="profileSocial.public.loadErrorTitle"
            bodyKey="profileSocial.public.loadErrorBody"
            onRetry={refresh}
            retryLabelKey="common.retry"
          />
        </div>
      </main>
    )
  }

  const profile = state.profile
  const relationshipData =
    relationship.status === 'ready' ? relationship.relationship : null

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="min-h-dvh bg-paper px-4 pt-[calc(var(--safe-top)+24px)] pb-[calc(1.5rem+var(--safe-bottom))] md:px-8 md:py-8"
    >
      <div className="mx-auto w-full max-w-[720px] space-y-4 min-[800px]:max-w-[1080px]">
        {header}

        {notice ? (
          <p
            role={notice.tone === 'error' ? 'alert' : 'status'}
            className={`rounded-md px-4 py-3 text-sm font-semibold ${
              notice.tone === 'error'
                ? 'bg-[var(--danger-soft)] text-danger'
                : 'bg-mint text-teal'
            }`}
          >
            {t(notice.key)}
          </p>
        ) : null}

        <PullToRefresh
          onRefresh={refresh}
          refreshing={refreshing}
          disabled={isDesktop || blocked}
        >
          {blocked ? (
            <div className="rounded-card border border-line bg-white p-6 text-center">
              <span
                className="mx-auto grid size-14 place-items-center rounded-full bg-[var(--danger-soft)] text-danger"
                aria-hidden="true"
              >
                <UserMinus size={26} weight={kuniriIconWeight} />
              </span>
              <h1 className="mt-4 text-[clamp(1.7rem,4vw,2.2rem)]">
                {t('profileSocial.public.blockedTitle')}
              </h1>
              <p className="mx-auto mt-2 max-w-[52ch] text-[15px] leading-relaxed text-muted">
                {t('profileSocial.public.blockedBody')}
              </p>
              <button
                type="button"
                className="btn-secondary mt-6"
                onClick={() => {
                  setConfirmError(null)
                  setConfirm('unblock')
                }}
              >
                {t('profileSocial.public.unblock')}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <section className="rounded-[28px] border border-line bg-white p-4 shadow-card min-[800px]:p-8">
                <div className="flex flex-col items-center text-center min-[800px]:flex-row min-[800px]:items-start min-[800px]:text-left">
                  <UserAvatar
                    name={formatPerson(profile)}
                    src={profile.avatarUrl}
                    size="xl"
                  />
                  <div className="mt-4 min-w-0 min-[800px]:mt-0 min-[800px]:ml-7 min-[800px]:flex-1">
                    <h1 className="text-[26px] leading-none min-[800px]:text-4xl">
                      {formatPerson(profile)}
                    </h1>
                    <p className="mt-1.5 font-bold text-teal">
                      @{profile.nickname}
                    </p>
                    {profile.city ? (
                      <p className="mt-2 text-sm text-muted">{profile.city}</p>
                    ) : null}
                    {profile.age != null ? (
                      <p className="mt-2 text-sm text-muted">
                        {t('profileSocial.profile.ageYears', {
                          count: profile.age,
                        })}
                      </p>
                    ) : null}
                  </div>
                  <div className="mt-4 w-full min-[800px]:mt-0 min-[800px]:w-[260px]">
                    <div className="text-left">
                      <ProfileMetrics
                        rating={profile.rating}
                        ratingCount={profile.ratingCount ?? 0}
                        activitiesCreated={profile.activitiesCreated ?? 0}
                        activitiesJoined={profile.activitiesJoined ?? 0}
                        activitiesParticipated={
                          profile.activitiesParticipated ?? 0
                        }
                      />
                    </div>
                    <div className="mt-3 space-y-2">
                      <FriendActions
                        relationship={relationship}
                        busy={friendBusy || messageBusy}
                        errorKey={friendError}
                        onAdd={() => void sendFriendRequest()}
                        onAccept={() => void decideRequest(true)}
                        onReject={() => void decideRequest(false)}
                        onMessage={() => void openMessage()}
                        onRetry={() => void reloadRelationship()}
                      />
                    </div>
                  </div>
                </div>
              </section>

              {(profile.bio || profile.languages.length > 0 || profile.interests.length > 0) && (
                <div className="space-y-4 min-[800px]:grid min-[800px]:grid-cols-2 min-[800px]:gap-4 min-[800px]:space-y-0">
                  {profile.bio ? (
                    <ProfileSection
                      title={t('profileSocial.public.about')}
                      body={profile.bio}
                      desktop={isDesktop}
                    />
                  ) : null}
                  {profile.languages.length > 0 ? (
                    <ProfileSection
                      title={t('profileSocial.public.languages')}
                      body={profile.languages.join(' · ')}
                      desktop={isDesktop}
                    />
                  ) : null}
                  {profile.interests.length > 0 ? (
                    <ProfileSection
                      title={t('profileSocial.public.interests')}
                      body={profile.interests.join(' · ')}
                      desktop={isDesktop}
                    />
                  ) : null}
                </div>
              )}

              {relationshipData?.status !== 'self' ? (
                <div className="flex flex-wrap gap-2 px-1 py-2">
                  <button
                    type="button"
                    className="btn-ghost min-h-11 px-3 text-sm"
                    onClick={() => {
                      setReportError(null)
                      setReportOpen(true)
                    }}
                  >
                    <Flag size={18} weight={kuniriIconWeight} aria-hidden="true" />
                    {t('profileSocial.public.report')}
                  </button>
                  <button
                    type="button"
                    className="btn-ghost min-h-11 px-3 text-sm text-danger hover:bg-[var(--danger-soft)]"
                    onClick={() => {
                      setConfirmError(null)
                      setConfirm('block')
                    }}
                  >
                    <UserMinus
                      size={18}
                      weight={kuniriIconWeight}
                      aria-hidden="true"
                    />
                    {t('profileSocial.public.block')}
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </PullToRefresh>
      </div>

      <ConfirmDialog
        open={confirm === 'block'}
        title={t('profileSocial.public.blockTitle', {
          name: formatPerson(profile),
        })}
        body={t('profileSocial.public.blockBody')}
        confirmLabel={t('profileSocial.public.blockConfirm')}
        busy={confirmBusy}
        danger
        errorKey={confirmError}
        onConfirm={() => void confirmBlockState()}
        onClose={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'unblock'}
        title={t('profileSocial.public.unblockTitle', {
          name: formatPerson(profile),
        })}
        body={t('profileSocial.public.unblockBody')}
        confirmLabel={t('profileSocial.public.unblockConfirm')}
        busy={confirmBusy}
        errorKey={confirmError}
        onConfirm={() => void confirmBlockState()}
        onClose={() => setConfirm(null)}
      />
      {reportOpen ? (
        <ReportDialog
          name={formatPerson(profile)}
          busy={reportBusy}
          errorKey={reportError}
          onSubmit={(reason, description) =>
            void submitReport(reason, description)
          }
          onClose={() => setReportOpen(false)}
        />
      ) : null}
    </main>
  )
}

function ProfileSection({
  title,
  body,
  desktop,
}: {
  title: string
  body: string
  desktop?: boolean
}) {
  return (
    <section
      className={
        desktop
          ? 'rounded-card border border-line bg-white p-5'
          : 'px-1 py-1'
      }
    >
      <h2 className="text-base font-extrabold text-deep">{title}</h2>
      <p className="mt-2 whitespace-pre-wrap text-[15px] leading-relaxed text-muted">
        {body}
      </p>
    </section>
  )
}

interface FriendActionsProps {
  relationship: RelationshipState
  busy: boolean
  errorKey: string | null
  onAdd: () => void
  onAccept: () => void
  onReject: () => void
  onMessage: () => void
  onRetry: () => void
}

function FriendActions({
  relationship,
  busy,
  errorKey,
  onAdd,
  onAccept,
  onReject,
  onMessage,
  onRetry,
}: FriendActionsProps) {
  const { t } = useTranslation()

  if (relationship.status === 'loading') {
    return (
      <LoadingState compact labelKey="common.loading" />
    )
  }
  if (relationship.status === 'error') {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <p role="alert" className="text-sm font-semibold text-danger">
          {t('profileSocial.public.relationshipError')}
        </p>
        <button type="button" className="btn-ghost min-h-11 px-2 text-sm" onClick={onRetry}>
          {t('common.retry')}
        </button>
      </div>
    )
  }

  const { status, direction, requestId } = relationship.relationship

  return (
    <>
      {status === 'accepted' ? (
        <button
          type="button"
          className="btn-primary w-full"
          disabled={busy}
          aria-busy={busy}
          onClick={onMessage}
        >
          <ChatCircle size={20} weight={kuniriIconWeight} aria-hidden="true" />
          {t('profileSocial.public.message')}
        </button>
      ) : null}

      {status === 'pending' && direction === 'incoming' ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-primary min-h-11 flex-1 px-3 text-sm"
            disabled={busy || !requestId}
            onClick={onAccept}
          >
            {t('profileSocial.public.acceptRequest')}
          </button>
          <button
            type="button"
            className="btn-secondary min-h-11 flex-1 px-3 text-sm"
            disabled={busy || !requestId}
            onClick={onReject}
          >
            {t('profileSocial.public.rejectRequest')}
          </button>
        </div>
      ) : null}

      {status === 'pending' && direction !== 'incoming' ? (
        <button type="button" className="btn-secondary w-full" disabled>
          {t('profileSocial.public.requestSent')}
        </button>
      ) : null}

      {(status === 'none' || status === 'rejected') ? (
        <button
          type="button"
          className="btn-primary w-full"
          disabled={busy}
          aria-busy={busy}
          onClick={onAdd}
        >
          <UserPlus size={20} weight={kuniriIconWeight} aria-hidden="true" />
          {busy
            ? t('profileSocial.public.adding')
            : t('profileSocial.public.addFriend')}
        </button>
      ) : null}

      {errorKey ? (
        <p role="alert" className="text-sm font-semibold text-danger">
          {t(errorKey)}
        </p>
      ) : null}
    </>
  )
}
